import React, { useState } from 'react'
import { MagnifyingGlassIcon, BookOpenIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, GlobeAltIcon, CalculatorIcon, DocumentMagnifyingGlassIcon, ArrowUturnLeftIcon, ScaleIcon } from '@heroicons/react/24/outline'
import { ligieService, type FraccionArancelaria } from '../../../services/ligieService'
import { toast } from 'react-hot-toast'
import { NOMBRES_CAPITULOS } from './capitulosLigie'
import { ModalReglasLigie } from '../../../componentes/modales/ModalReglasLigie'
import { ModalNotasCapitulo } from '../../../componentes/modales/ModalNotasCapitulo'
import { obtenerNotasCapitulo } from './notasCapitulosLigie'


const ESTRUCTURA_LIGIE = [
  { seccion: 'I', titulo: 'ANIMALES VIVOS Y PRODUCTOS DEL REINO ANIMAL', capitulos: '01 al 05' },
  { seccion: 'II', titulo: 'PRODUCTOS DEL REINO VEGETAL', capitulos: '06 al 14' },
  { seccion: 'III', titulo: 'GRASAS Y ACEITES ANIMALES O VEGETALES', capitulos: '15' },
  { seccion: 'IV', titulo: 'PRODUCTOS DE LAS INDUSTRIAS ALIMENTARIAS', capitulos: '16 al 24' },
  { seccion: 'V', titulo: 'PRODUCTOS MINERALES', capitulos: '25 al 27' },
  { seccion: 'VI', titulo: 'PRODUCTOS DE LAS INDUSTRIAS QUÍMICAS', capitulos: '28 al 38' },
  { seccion: 'VII', titulo: 'PLÁSTICOS Y SUS MANUFACTURAS; CAUCHO', capitulos: '39 al 40' },
  { seccion: 'VIII', titulo: 'PIELES, CUEROS, PELETERÍA Y MANUFACTURAS', capitulos: '41 al 43' },
  { seccion: 'IX', titulo: 'MADERA, CARBÓN VEGETAL Y MANUFACTURAS', capitulos: '44 al 46' },
  { seccion: 'X', titulo: 'PASTA DE MADERA; PAPEL O CARTÓN', capitulos: '47 al 49' },
  { seccion: 'XI', titulo: 'MATERIAS TEXTILES Y SUS MANUFACTURAS', capitulos: '50 al 63' },
  { seccion: 'XII', titulo: 'CALZADO, SOMBREROS, PARAGUAS', capitulos: '64 al 67' },
  { seccion: 'XIII', titulo: 'MANUFACTURAS DE PIEDRA, YESO, CEMENTO', capitulos: '68 al 70' },
  { seccion: 'XIV', titulo: 'PERLAS FINAS, PIEDRAS PRECIOSAS, METALES', capitulos: '71' },
  { seccion: 'XV', titulo: 'METALES COMUNES Y MANUFACTURAS', capitulos: '72 al 83' },
  { seccion: 'XVI', titulo: 'MÁQUINAS Y APARATOS, MATERIAL ELÉCTRICO', capitulos: '84 al 85' },
  { seccion: 'XVII', titulo: 'MATERIAL DE TRANSPORTE', capitulos: '86 al 89' },
  { seccion: 'XVIII', titulo: 'INSTRUMENTOS Y APARATOS DE ÓPTICA', capitulos: '90 al 92' },
  { seccion: 'XIX', titulo: 'ARMAS, MUNICIONES, Y SUS PARTES', capitulos: '93' },
  { seccion: 'XX', titulo: 'MERCANCÍAS Y PRODUCTOS DIVERSOS', capitulos: '94 al 96' },
  { seccion: 'XXI', titulo: 'OBJETOS DE ARTE O COLECCIÓN', capitulos: '97' },
  { seccion: 'XXII', titulo: 'OPERACIONES ESPECIALES', capitulos: '98' }
];

const obtenerArrayCapitulos = (rango: string) => {
  if (rango.includes('al')) {
    const [inicio, fin] = rango.split(' al ').map(Number);
    return Array.from({ length: fin - inicio + 1 }, (_, i) => (inicio + i).toString().padStart(2, '0'));
  }
  return [rango.padStart(2, '0')];
};

const normalizarCodigoLigie = (value: string) => String(value || '').replace(/\D/g, '')

const formatearCodigoLigie = (value: string) => {
  const limpio = normalizarCodigoLigie(value)
  if (limpio.length === 2) return limpio
  if (limpio.length === 4) return limpio
  if (limpio.length === 6) return `${limpio.slice(0, 4)}.${limpio.slice(4, 6)}`
  if (limpio.length === 8) return `${limpio.slice(0, 4)}.${limpio.slice(4, 6)}.${limpio.slice(6, 8)}`
  if (limpio.length >= 10) return `${limpio.slice(0, 4)}.${limpio.slice(4, 6)}.${limpio.slice(6, 8)}.${limpio.slice(8, 10)}`
  return ''
}

const PaginaBuscadorLigie: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'buscador' | 'explorador'>('buscador')
  const [busqueda, setBusqueda] = useState('')
  const [resultados, setResultados] = useState<FraccionArancelaria[]>([])
  const [loading, setLoading] = useState(false)
  const [fraccionSeleccionada, setFraccionSeleccionada] = useState<FraccionArancelaria | null>(null)
  const [jerarquia, setJerarquia] = useState<FraccionArancelaria[]>([])
  const [hijos, setHijos] = useState<FraccionArancelaria[]>([])
  const [seccionAbierta, setSeccionAbierta] = useState<string | null>(null)
  const [mostrarModalReglasLigie, setMostrarModalReglasLigie] = useState(false)
  const [mostrarModalNotas, setMostrarModalNotas] = useState(false)
  const [capituloNotasActivo, setCapituloNotasActivo] = useState<string>('01')

  const handleAbrirNotasCapitulo = (cap: string) => {
    const limpio = (cap || '').replace(/\D/g, '').slice(0, 2).padStart(2, '0')
    setCapituloNotasActivo(limpio || '01')
    setMostrarModalNotas(true)
  }

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!busqueda.trim()) return

    setActiveTab('buscador')
    setLoading(true)
    setFraccionSeleccionada(null)
    try {
      const data = await ligieService.buscarFracciones(busqueda)
      setResultados(data)
      const codigoExacto = formatearCodigoLigie(busqueda)
      const coincidenciaExacta = codigoExacto
        ? data.find((item) => item.codigo === codigoExacto)
        : undefined

      if (coincidenciaExacta) {
        handleSeleccionar(coincidenciaExacta.codigo)
      }

      if (data.length === 0) {
        toast('No se encontraron resultados', { icon: '🔍' })
      }
    } catch (error) {
      toast.error('Error al buscar fracciones')
    } finally {
      setLoading(false)
    }
  }

  const handleSeleccionar = async (codigo: string) => {
    setLoading(true)
    try {
      const data = await ligieService.obtenerDetalleFraccion(codigo)
      if (data) {
        setFraccionSeleccionada(data.detalle)
        setJerarquia(data.jerarquia)
        setHijos(data.hijos || [])
      }
    } catch (error) {
      toast.error('Error al cargar detalles')
    } finally {
      setLoading(false)
    }
  }

  const handleSeleccionarDesdeExplorador = (capitulo: string) => {
    setBusqueda(capitulo)
    setActiveTab('buscador')
    // Simulamos la búsqueda del capítulo automáticamente

    // Necesitamos asegurarnos de que la búsqueda use el nuevo valor
    setLoading(true)
    setFraccionSeleccionada(null)
    ligieService.buscarFracciones(capitulo).then(data => {
      setResultados(data)
      if (data.length === 0) {
        toast('No se encontraron resultados', { icon: '🔍' })
      } else {
        // Seleccionamos automáticamente el capítulo para ver sus detalles
        handleSeleccionar(capitulo)
      }
    }).catch(() => {
      toast.error('Error al buscar fracciones')
    }).finally(() => {
      setLoading(false)
    })
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="relative overflow-hidden rounded-[24px] border border-slate-200/60 bg-white shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-white/[0.08] dark:bg-[#0c0d12]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-none" />

        <div className="relative px-5 py-6 md:px-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-sm border border-indigo-500/30 bg-indigo-500/10 px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] text-indigo-700 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-indigo-400">
                <GlobeAltIcon className="h-4 w-4" />
                INTELIGENCIA ARANCELARIA
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-zinc-100">
                  Buscador LIGIE
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-zinc-400">
                  Motor de búsqueda para la correcta clasificación de mercancías. Consulta códigos arancelarios, NICOs y regulaciones aplicables.
                </p>
              </div>
            </div>

            <div className="w-full xl:max-w-lg">
              <form onSubmit={handleBuscar} className="relative flex w-full items-center">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Código (01012101 o 0101.21.01) o descripción..."
                  className="block w-full rounded-xl border border-slate-300 bg-white py-3.5 pl-11 pr-24 text-sm font-medium text-slate-900 shadow-sm outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={loading}
                  onClick={() => setActiveTab('buscador')}
                  className="absolute right-2 top-2 bottom-2 rounded-lg bg-sky-600 px-4 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-sky-500 disabled:opacity-50"
                >
                  {loading ? 'Buscando...' : 'Analizar'}
                </button>
              </form>
            </div>
          </div>

          {/* Selector de Tabs y Acceso a Reglas LIGIE */}
          <div className="mt-8 flex items-center justify-between border-b border-slate-200 dark:border-zinc-800 pb-2">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('buscador')}
                className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors border-b-2 -mb-2.5 ${activeTab === 'buscador'
                  ? 'border-sky-500 text-sky-600 dark:border-sky-400 dark:text-sky-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-zinc-500 dark:hover:text-zinc-300'
                  }`}
              >
                Búsqueda y Análisis
              </button>
              <button
                onClick={() => setActiveTab('explorador')}
                className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors border-b-2 -mb-2.5 ${activeTab === 'explorador'
                  ? 'border-sky-500 text-sky-600 dark:border-sky-400 dark:text-sky-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-zinc-500 dark:hover:text-zinc-300'
                  }`}
              >
                Explorador LIGIE General
              </button>
            </div>

            <div className="flex items-center gap-2 shrink-0 -mb-1">
              <button
                type="button"
                onClick={() => handleAbrirNotasCapitulo('01')}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-bold transition-all shadow-xs cursor-pointer shrink-0"
                title="Consultar Notas Legales Oficiales de Sección y de Capítulo (98 Capítulos)"
              >
                <BookOpenIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Notas de Capítulo (1 a 98)</span>
              </button>

              <button
                type="button"
                onClick={() => setMostrarModalReglasLigie(true)}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-bold transition-all shadow-xs cursor-pointer shrink-0"
                title="Consultar Artículo 2 LIGIE: Las 6 Reglas Generales y 10 Complementarias"
              >
                <ScaleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span>Reglas LIGIE (Art. 2)</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'buscador' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel Izquierdo: Resultados */}
          <div className="lg:col-span-1 flex flex-col overflow-hidden rounded-[24px] border border-slate-200/60 bg-white shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] max-h-[800px]">
            <div className="border-b border-slate-200/60 bg-slate-50/50 px-5 py-4 dark:border-zinc-800/50 dark:bg-zinc-900/30">
              <h3 className="flex items-center justify-between text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-zinc-100">
                <span>Coincidencias</span>
                <span className="rounded-sm bg-slate-200 px-2 py-0.5 text-[9px] font-bold text-slate-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {resultados.length} REGISTROS
                </span>
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
              {resultados.length > 0 ? (
                <ul className="space-y-2">
                  {resultados.map((fraccion) => {
                    // Manejar tanto el caso de búsqueda por '0101' como por '01.01'
                    const seleccionado = fraccionSeleccionada?.codigo.replace('.', '') === fraccion.codigo.replace('.', '')
                    return (
                      <li
                        key={fraccion.codigo}
                        className={`
                        group cursor-pointer rounded-xl border px-4 py-3 transition-all duration-200
                        ${seleccionado
                            ? 'border-sky-300 bg-sky-50 shadow-sm dark:border-zinc-700 dark:bg-zinc-800'
                            : 'border-slate-200/60 bg-white hover:border-sky-300 hover:shadow-md dark:border-zinc-800/50 dark:bg-zinc-900/50 dark:hover:border-zinc-700 dark:hover:bg-zinc-800'
                          }
                      `}
                        onClick={() => handleSeleccionar(fraccion.codigo)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className={`text-sm font-bold font-mono tracking-tight ${seleccionado ? 'text-sky-700 dark:text-zinc-100' : 'text-slate-900 dark:text-zinc-300 group-hover:text-sky-600 dark:group-hover:text-white'}`}>
                              {fraccion.codigo}
                            </p>
                            <p className={`mt-1 text-xs line-clamp-2 ${seleccionado ? 'text-sky-800/80 dark:text-zinc-400' : 'text-slate-500 dark:text-zinc-500'}`}>
                              {fraccion.descripcion}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${seleccionado
                            ? 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-200'
                            : 'border-slate-200 bg-slate-100 text-slate-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400'
                            }`}>
                            {fraccion.nivel}
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className="flex h-full flex-col items-center justify-center p-8 text-center opacity-60">
                  <DocumentMagnifyingGlassIcon className="h-12 w-12 text-slate-400 dark:text-zinc-600 mb-3" />
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-zinc-500">
                    Sistema en espera
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Panel Derecho: Detalles */}
          <div className="lg:col-span-2">
            {fraccionSeleccionada ? (
              <div className="overflow-hidden rounded-[24px] border border-slate-200/60 bg-white shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] animate-fadeIn">

                {/* Breadcrumbs de Navegación y Botón Volver */}
                <div className="border-b border-slate-200/60 bg-slate-50/50 px-6 py-3 dark:border-zinc-800/50 dark:bg-zinc-900/30 flex justify-between items-center">
                  <nav className="flex overflow-x-auto custom-scrollbar pb-1" aria-label="Breadcrumb">
                    <ol className="flex items-center space-x-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-zinc-500">
                      {jerarquia.map((item, index) => (
                        <li key={item.codigo} className="flex items-center whitespace-nowrap">
                          {index > 0 && <ChevronRightIcon className="h-3 w-3 mx-1 text-slate-300 dark:text-zinc-600" />}
                          <button
                            onClick={() => handleSeleccionar(item.codigo)}
                            className="hover:text-sky-600 dark:hover:text-zinc-300 transition-colors"
                            title={item.descripcion}
                          >
                            {item.codigo}
                          </button>
                        </li>
                      ))}
                      <li className="flex items-center whitespace-nowrap">
                        <ChevronRightIcon className="h-3 w-3 mx-1 text-slate-300 dark:text-zinc-600" />
                        <span className="text-sky-600 dark:text-zinc-200">{fraccionSeleccionada.codigo}</span>
                      </li>
                    </ol>
                  </nav>

                  <button
                    onClick={() => {
                      setFraccionSeleccionada(null)
                      setResultados([])
                      setBusqueda('')
                    }}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
                  >
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                    Ver Estructura
                  </button>
                </div>

                <div className="p-6 md:p-8">
                  {/* Header del Detalle */}
                  <div className="mb-8">
                    <div className="flex flex-wrap items-end gap-4">
                      <h2 className="text-3xl font-bold font-mono tracking-tight text-slate-900 dark:text-zinc-100">
                        {fraccionSeleccionada.codigo}
                      </h2>
                      {fraccionSeleccionada.nico && (
                        <div className="mb-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-xs font-bold tracking-widest text-indigo-700 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-300">
                          NICO: {fraccionSeleccionada.nico}
                        </div>
                      )}
                    </div>
                    <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-zinc-400">
                      {fraccionSeleccionada.descripcion}
                    </p>
                  </div>

                  {/* Bento Card: Notas Legales de Capítulo Vinculantes (Regla 1) */}
                  {(() => {
                    const capCodigo = fraccionSeleccionada.codigo.replace(/\D/g, '').slice(0, 2).padStart(2, '0');
                    const notaLegal = obtenerNotasCapitulo(capCodigo);
                    return (
                      <div className="mb-8 rounded-2xl border border-indigo-500/30 bg-gradient-to-r from-indigo-500/5 via-sky-500/5 to-purple-500/5 p-4 sm:p-5 dark:border-indigo-500/20 dark:bg-zinc-900/40">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-500/20">
                              <BookOpenIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700 dark:text-indigo-400">
                                  Regla General 1 • Notas Legales del Capítulo {capCodigo}
                                </span>
                                <span className="rounded-sm bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.2 text-[9px] font-bold">
                                  Sección {notaLegal?.seccion || 'LIGIE'}
                                </span>
                              </div>
                              <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
                                {notaLegal?.titulo || NOMBRES_CAPITULOS[capCodigo] || `Capítulo ${capCodigo}`}
                              </h4>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleAbrirNotasCapitulo(capCodigo)}
                            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition shadow-xs cursor-pointer self-start sm:self-auto shrink-0"
                          >
                            <BookOpenIcon className="h-4 w-4" />
                            <span>Ver Notas del Capítulo {capCodigo}</span>
                          </button>
                        </div>

                        {notaLegal?.criterioClave && (
                          <p className="text-xs text-slate-600 dark:text-zinc-400 bg-white/70 dark:bg-black/20 p-2.5 rounded-xl border border-indigo-500/10 mt-3 leading-relaxed">
                            <strong className="text-indigo-700 dark:text-indigo-300">Criterio Legal / Exclusiones: </strong>
                            {notaLegal.criterioClave}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Grid de Datos Operativos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-zinc-800/50 dark:bg-zinc-900/50">
                      <h4 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-zinc-500 mb-4">
                        <CalculatorIcon className="h-4 w-4" />
                        Parámetros Base
                      </h4>
                      <dl className="space-y-3">
                        <div className="flex justify-between items-center border-b border-slate-200/60 dark:border-zinc-800 pb-2">
                          <dt className="text-xs font-medium text-slate-500 dark:text-zinc-400">Nivel Estructural</dt>
                          <dd className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100">{fraccionSeleccionada.nivel}</dd>
                        </div>
                        <div className="flex justify-between items-center border-b border-slate-200/60 dark:border-zinc-800 pb-2">
                          <dt className="text-xs font-medium text-slate-500 dark:text-zinc-400">Unidad de Medida</dt>
                          <dd className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100">{fraccionSeleccionada.unidad_medida || 'N/A'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="rounded-xl border border-sky-100 bg-sky-50/50 p-5 shadow-sm dark:border-zinc-700/50 dark:bg-zinc-800/50">
                      <h4 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-zinc-300 mb-4">
                        <GlobeAltIcon className="h-4 w-4" />
                        Aranceles Aplicables
                      </h4>
                      <dl className="space-y-3">
                        <div className="flex justify-between items-center border-b border-sky-200/60 dark:border-zinc-700 pb-2">
                          <dt className="text-xs font-medium text-sky-800 dark:text-zinc-400">Importación (IMP)</dt>
                          <dd className="text-sm font-bold text-sky-900 dark:text-zinc-100">{fraccionSeleccionada.arancel_imp || 'Exento'}</dd>
                        </div>
                        <div className="flex justify-between items-center border-b border-sky-200/60 dark:border-zinc-700 pb-2">
                          <dt className="text-xs font-medium text-sky-800 dark:text-zinc-400">Exportación (EXP)</dt>
                          <dd className="text-sm font-bold text-sky-900 dark:text-zinc-100">{fraccionSeleccionada.arancel_exp || 'Exento'}</dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  {/* Sub-niveles o NICO's */}
                  {hijos && hijos.length > 0 && (
                    <div>
                      <h3 className="flex items-center gap-3 text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-zinc-100 mb-4">
                        {fraccionSeleccionada.nivel === 'fraccion' ? 'Números de Identificación Comercial (NICO)' : 'Desglose Estructural'}
                        <span className="rounded-sm bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-zinc-800 dark:text-zinc-400">
                          {hijos.length} SUB-NIVELES
                        </span>
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {hijos.map((hijo) => (
                          <div
                            key={hijo.codigo}
                            onClick={() => handleSeleccionar(hijo.codigo)}
                            className="group flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200/60 bg-white p-3 shadow-sm transition-all hover:border-sky-300 hover:shadow-md dark:border-zinc-800/50 dark:bg-zinc-900/50 dark:hover:border-zinc-700 dark:hover:bg-zinc-800"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-[10px] font-bold text-slate-500 group-hover:bg-sky-50 group-hover:text-sky-600 dark:bg-zinc-800 dark:text-zinc-400 dark:group-hover:bg-zinc-700 dark:group-hover:text-zinc-200">
                              {hijo.nico || hijo.codigo.split('.').pop()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-bold font-mono tracking-tight text-slate-900 transition-colors group-hover:text-sky-600 dark:text-zinc-100 dark:group-hover:text-white">
                                {hijo.codigo}
                              </p>
                              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500 line-clamp-2 dark:text-zinc-500">
                                {hijo.descripcion}
                              </p>
                            </div>
                            <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-sky-500 dark:text-zinc-600 dark:group-hover:text-zinc-400" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-[400px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-300 bg-white/50 text-center dark:border-zinc-800 dark:bg-zinc-900/30">
                <BookOpenIcon className="mb-4 h-12 w-12 text-slate-300 dark:text-zinc-700" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-zinc-400">
                  LIGIE EN MODO DE ESPERA
                </h3>
                <p className="mt-2 text-xs text-slate-500 dark:text-zinc-600">
                  Selecciona una fracción arancelaria del panel izquierdo o usa el Explorador LIGIE.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* VISTA DEL EXPLORADOR LIGIE */
        <div className="overflow-hidden rounded-[24px] border border-slate-200/60 bg-white shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] animate-fadeIn p-6 md:p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400">
              <BookOpenIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-zinc-100">
                Estructura General de la LIGIE
              </h3>
              <p className="text-base text-slate-500 dark:text-zinc-400">
                Explora las 22 Secciones y 98 Capítulos que conforman la Tarifa de los Impuestos Generales de Importación y de Exportación.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start">
            {ESTRUCTURA_LIGIE.map((sec) => {
              const estaAbierta = seccionAbierta === sec.seccion;
              return (
                <div
                  key={sec.seccion}
                  className={`group relative flex flex-col overflow-hidden rounded-2xl border transition-all ${
                    estaAbierta
                      ? 'border-sky-400 bg-white shadow-md dark:border-sky-500/50 dark:bg-zinc-800'
                      : 'border-slate-200/60 bg-slate-50/50 hover:border-sky-300 hover:bg-white dark:border-zinc-800/50 dark:bg-zinc-900/30 dark:hover:border-zinc-700 dark:hover:bg-zinc-800'
                  }`}
                >
                  <div
                    className="p-5 cursor-pointer flex flex-col"
                    onClick={() => setSeccionAbierta(estaAbierta ? null : sec.seccion)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
                        Sección {sec.seccion}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="rounded-md bg-slate-200/50 px-2 py-1 text-[10px] font-bold tracking-widest text-slate-600 dark:bg-zinc-800 dark:text-zinc-400">
                          CAP: {sec.capitulos}
                        </span>
                        <span className="text-slate-400 dark:text-zinc-500">
                          {estaAbierta ? (
                            <ChevronUpIcon className="h-3.5 w-3.5 text-sky-500" />
                          ) : (
                            <ChevronDownIcon className="h-3.5 w-3.5 group-hover:text-sky-500 transition-colors" />
                          )}
                        </span>
                      </div>
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-zinc-100 leading-snug">
                      {sec.titulo}
                    </h4>
                  </div>

                  {/* Acordeón de Capítulos */}
                  {estaAbierta && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50/30 dark:border-zinc-700/50 dark:bg-zinc-800/30 animate-fadeIn shrink-0">
                      <p className="text-[11px] font-bold text-slate-500 mb-2.5 dark:text-zinc-400 uppercase tracking-wider">
                        SELECCIONA UN CAPÍTULO PARA EXPLORAR
                      </p>
                      <div className="flex flex-col gap-2">
                        {obtenerArrayCapitulos(sec.capitulos).map((cap) => (
                          <div key={cap} className="flex items-center gap-1.5 w-full">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSeleccionarDesdeExplorador(cap);
                              }}
                              className="flex-1 flex items-center text-left px-3 py-2 text-xs font-mono font-bold text-sky-700 bg-sky-100/50 border border-sky-200 rounded-lg hover:bg-sky-500 hover:text-white hover:border-sky-500 transition-colors dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30 dark:hover:bg-sky-500 dark:hover:text-white group/btn min-w-0"
                              title={`Explorar fracciones del Capítulo ${cap}`}
                            >
                              <span className="shrink-0 w-14 font-mono font-bold">Cap. {cap}</span>
                              <span className="ml-1 font-sans font-medium text-[11px] text-slate-700 dark:text-zinc-300 group-hover/btn:text-white line-clamp-2 leading-tight">
                                {NOMBRES_CAPITULOS[cap] || 'Cargando...'}
                              </span>
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAbrirNotasCapitulo(cap);
                              }}
                              className="shrink-0 flex items-center gap-1 px-2.5 py-2 text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200/80 rounded-lg hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-500/30 cursor-pointer shadow-xs"
                              title={`Ver Notas Legales del Capítulo ${cap}`}
                            >
                              <BookOpenIcon className="w-3.5 h-3.5" />
                              <span>Notas</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Modal Informativo: Artículo 2 LIGIE - Reglas Generales y Complementarias */}
      <ModalReglasLigie
        abierto={mostrarModalReglasLigie}
        onCerrar={() => setMostrarModalReglasLigie(false)}
        moduloOrigen="inteligencia"
      />
      {/* Modal Informativo: Compendio Oficial de Notas Legales de Sección y Capítulo */}
      <ModalNotasCapitulo
        abierto={mostrarModalNotas}
        onCerrar={() => setMostrarModalNotas(false)}
        capituloInicial={capituloNotasActivo}
      />
    </div>
  )
}

export default PaginaBuscadorLigie
